// Job-description workspace: extraction progress while the pipeline runs,
// then editor tabs (Details / Sections / Contact) beside the live formatted
// preview, with template pick + generate. Mirrors the resume JobDetail flow.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  AlignLeft,
  ArrowLeft,
  Check,
  ChevronDown,
  ExternalLink,
  FileDown,
  FileText,
  Minus,
  Plus,
  RotateCcw,
  Sparkles,
  Trash2,
  TriangleAlert,
  Users,
  X,
} from "lucide-react";
import type {
  AgpJobDescription,
  JdContact,
  JdSection,
  OutputInfo,
  ResumeJobStatus,
  TemplateInfo,
} from "@agp/shared";
import { useApi } from "@/hooks/useApi";
import { STATUS_LABEL } from "@/lib/jobs";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Input, Label, Select, Textarea } from "@/components/ui/input";
import { ContentLinesEditor, SectionCard, StringListEditor } from "@/components/review/editors";
import { JdPreview } from "@/components/templates/JdPreview";

interface JdDetailData {
  id: string;
  status: ResumeJobStatus;
  sourceKind: "paste" | "file" | "url";
  sourceFilename: string | null;
  sourceUrl: string | null;
  warnings: string[];
  error: string | null;
}

type TabKey = "details" | "sections" | "contact";

const TABS: Array<{ key: TabKey; label: string; icon: typeof AlignLeft }> = [
  { key: "details", label: "Details", icon: FileText },
  { key: "sections", label: "Sections", icon: AlignLeft },
  { key: "contact", label: "Contact", icon: Users },
];

function DetailsRowsEditor({
  items,
  onChange,
}: {
  items: Array<{ label: string; value: string }>;
  onChange: (items: Array<{ label: string; value: string }>) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      {items.map((row, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input
            value={row.label}
            placeholder="Label (Salary, Business Area…)"
            className="max-w-[220px]"
            onChange={(e) => {
              const next = [...items];
              next[i] = { ...row, label: e.target.value };
              onChange(next);
            }}
          />
          <Input
            value={row.value}
            placeholder="Value"
            onChange={(e) => {
              const next = [...items];
              next[i] = { ...row, value: e.target.value };
              onChange(next);
            }}
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            aria-label="Remove detail"
            onClick={() => onChange(items.filter((_, j) => j !== i))}
          >
            <Trash2 size={14} />
          </Button>
        </div>
      ))}
      <Button
        variant="secondary"
        size="sm"
        className="self-start"
        onClick={() => onChange([...items, { label: "", value: "" }])}
      >
        <Plus size={14} /> Add detail row
      </Button>
    </div>
  );
}

function SectionsEditor({
  items,
  onChange,
}: {
  items: JdSection[];
  onChange: (items: JdSection[]) => void;
}) {
  const move = (from: number, to: number) => {
    if (to < 0 || to >= items.length) return;
    const next = [...items];
    const [s] = next.splice(from, 1);
    next.splice(to, 0, s!);
    onChange(next);
  };
  return (
    <div className="flex flex-col gap-3">
      {items.map((section, i) => (
        <div key={i} className="rounded-lg border border-border bg-surface p-4">
          <div className="flex items-center gap-2">
            <Input
              value={section.heading}
              placeholder="Section heading"
              onChange={(e) => {
                const next = [...items];
                next[i] = { ...section, heading: e.target.value };
                onChange(next);
              }}
            />
            <div className="flex shrink-0 items-center gap-0.5">
              <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Move up" onClick={() => move(i, i - 1)}>
                ↑
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Move down" onClick={() => move(i, i + 1)}>
                ↓
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                aria-label="Remove section"
                onClick={() => onChange(items.filter((_, j) => j !== i))}
              >
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
          <div className="mt-2">
            <ContentLinesEditor
              items={section.lines}
              onChange={(lines) => {
                const next = [...items];
                next[i] = { ...section, lines };
                onChange(next);
              }}
            />
          </div>
        </div>
      ))}
      <Button
        variant="secondary"
        size="sm"
        className="self-start"
        onClick={() => onChange([...items, { heading: "", lines: [] }])}
      >
        <Plus size={14} /> Add section
      </Button>
    </div>
  );
}

export function JdDetail() {
  const { id = "" } = useParams();
  const api = useApi();
  const navigate = useNavigate();

  const jobQuery = useQuery({
    queryKey: ["jd-job", id],
    queryFn: () => api.get<JdDetailData>(`/api/jd-jobs/${id}`),
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === "extracting" || s === "created" ? 1500 : false;
    },
  });
  const status = jobQuery.data?.status;

  const parsedQuery = useQuery({
    queryKey: ["jd-parsed", id],
    queryFn: () =>
      api.get<{
        extracted: AgpJobDescription | null;
        edited: AgpJobDescription | null;
        warnings: string[];
      }>(`/api/jd-jobs/${id}/parsed`),
    enabled: status === "ready_for_review",
    staleTime: Infinity,
  });

  const [jd, setJd] = useState<AgpJobDescription | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [warningsDismissed, setWarningsDismissed] = useState(false);
  const [saveState, setSaveState] = useState<"idle" | "dirty" | "saving" | "saved" | "error">("idle");
  const [activeTab, setActiveTab] = useState<TabKey>("details");
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestJd = useRef<AgpJobDescription | null>(null);

  useEffect(() => {
    if (parsedQuery.data && jd === null) {
      setJd(parsedQuery.data.edited ?? parsedQuery.data.extracted);
      setWarnings(parsedQuery.data.warnings);
    }
  }, [parsedQuery.data, jd]);

  const persist = useCallback(async () => {
    if (!latestJd.current) return;
    setSaveState("saving");
    try {
      const res = await api.patch<{ revisionId: string; warnings: string[] }>(
        `/api/jd-jobs/${id}/parsed`,
        { data: latestJd.current },
      );
      setWarnings(res.warnings);
      setSaveState("saved");
    } catch {
      setSaveState("error");
    }
  }, [api, id]);

  const updateJd = useCallback(
    (patch: Partial<AgpJobDescription>) => {
      setJd((prev) => {
        if (!prev) return prev;
        const next = { ...prev, ...patch };
        latestJd.current = next;
        return next;
      });
      setSaveState("dirty");
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => void persist(), 1200);
    },
    [persist],
  );

  useEffect(
    () => () => {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        void persist();
      }
    },
    [persist],
  );

  // Full current contact (arrays guaranteed by normalizeJd) for spread-patch
  // updates from the Contact tab.
  const contactBase = () => ({
    name: jd!.contact?.name ?? null,
    title: jd!.contact?.title ?? null,
    company: jd!.contact?.company ?? null,
    telephone: jd!.contact?.telephone ?? [],
    mobile: jd!.contact?.mobile ?? [],
    email: jd!.contact?.email ?? [],
    website: jd!.contact?.website ?? [],
  });

  const [discardOpen, setDiscardOpen] = useState(false);
  const [discardBusy, setDiscardBusy] = useState(false);
  const deleteJob = useCallback(async () => {
    setDiscardBusy(true);
    try {
      await api.delete(`/api/jd-jobs/${id}`);
      navigate("/jds");
    } finally {
      setDiscardBusy(false);
    }
  }, [api, id, navigate]);

  const templatesQuery = useQuery({
    queryKey: ["templates"],
    queryFn: () => api.get<{ templates: TemplateInfo[]; pdfAvailable: boolean }>("/api/templates"),
    enabled: status === "ready_for_review",
  });
  // JD documents can only render through JD templates.
  const templates = (templatesQuery.data?.templates ?? []).filter(
    (t) => t.definition.kind === "jd",
  );
  const pdfAvailable = templatesQuery.data?.pdfAvailable ?? false;
  const jdContactsQuery = useQuery({
    queryKey: ["jd-contacts"],
    queryFn: () => api.get<{ contacts: JdContact[] }>("/api/jd-contacts"),
    enabled: status === "ready_for_review",
  });
  const jdContacts = jdContactsQuery.data?.contacts ?? [];
  const [templateId, setTemplateId] = useState("");
  const [zoom, setZoom] = useState(1);
  const selectedTemplate = templates.find((t) => t.id === templateId) ?? templates[0] ?? null;

  const [generating, setGenerating] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const genRef = useRef<HTMLDivElement | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    if (!genOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (!genRef.current?.contains(e.target as Node)) setGenOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setGenOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [genOpen]);

  const generate = useCallback(
    async (format: "docx" | "pdf") => {
      if (!selectedTemplate) return;
      setGenOpen(false);
      setGenerating(true);
      setActionError(null);
      try {
        await persist();
        const res = await api.post<{ outputs: Array<{ id: string; format: "docx" | "pdf" }> }>(
          `/api/jd-jobs/${id}/generate`,
          { templateId: selectedTemplate.id, formats: [format] },
        );
        const outs = await api.get<{ outputs: OutputInfo[] }>(`/api/jd-jobs/${id}/outputs`);
        const target = outs.outputs.find((o) => o.id === res.outputs[0]?.id);
        if (target) {
          try {
            const fileRes = await fetch(target.downloadUrl);
            if (!fileRes.ok) throw new Error(`download failed (${fileRes.status})`);
            const blobUrl = URL.createObjectURL(await fileRes.blob());
            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = target.filename;
            a.click();
            URL.revokeObjectURL(blobUrl);
          } catch {
            window.location.assign(target.downloadUrl);
          }
        }
      } catch (err) {
        setActionError(err instanceof Error ? err.message : "Generation failed.");
      } finally {
        setGenerating(false);
      }
    },
    [api, id, selectedTemplate, persist],
  );

  const retryExtraction = useCallback(async () => {
    setRetrying(true);
    setActionError(null);
    try {
      await api.post(`/api/jd-jobs/${id}/extract`);
      await jobQuery.refetch();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Could not restart extraction.");
    } finally {
      setRetrying(false);
    }
  }, [api, id, jobQuery]);

  const saveLabel = useMemo(() => {
    switch (saveState) {
      case "dirty":
      case "saving":
        return "Saving…";
      case "saved":
        return "All changes saved";
      case "error":
        return "Save failed — edits retry on next change";
      default:
        return "";
    }
  }, [saveState]);

  if (jobQuery.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-4 w-36" />
        <Skeleton className="h-72 w-full rounded-lg" />
      </div>
    );
  }
  if (!jobQuery.data) {
    return (
      <EmptyState
        icon={<FileText className="h-5 w-5" strokeWidth={1.75} aria-hidden />}
        title="Job description not found"
        body="It may have been deleted, or the link is stale."
        action={
          <Button asChild variant="secondary">
            <Link to="/jds">Back to job descriptions</Link>
          </Button>
        }
      />
    );
  }
  const job = jobQuery.data;
  const statusMeta = STATUS_LABEL[job.status];
  const extracting = job.status === "extracting" || job.status === "created";
  const ready = job.status === "ready_for_review" && jd !== null;
  const title = jd?.title ?? job.sourceFilename ?? job.sourceUrl ?? "Job description";
  const sourceHref = job.sourceKind === "url" ? job.sourceUrl : null;

  return (
    <div className="flex flex-col gap-4">
      <Link
        to="/jds"
        className="inline-flex items-center gap-1.5 self-start text-[13.5px] font-semibold text-brand-on no-underline hover:underline"
      >
        <ArrowLeft size={15} strokeWidth={2} aria-hidden />
        Back to job descriptions
      </Link>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="truncate">{title}</h3>
            <Badge variant={statusMeta.variant}>
              {job.status === "ready_for_review" && <Check size={12} strokeWidth={2.5} aria-hidden />}
              {statusMeta.label}
            </Badge>
          </div>
          <p className="truncate text-[12px] text-text-2">
            {job.sourceFilename ?? job.sourceUrl ?? "Pasted text"}
            {saveLabel && ` · ${saveLabel}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => setDiscardOpen(true)}>
            <Trash2 size={14} strokeWidth={1.75} aria-hidden />
            {ready ? "Discard" : "Delete"}
          </Button>
          {ready && (
            <div ref={genRef} className="relative">
              <Button
                size="sm"
                disabled={generating || !selectedTemplate}
                aria-haspopup="true"
                aria-expanded={genOpen}
                onClick={() => setGenOpen((v) => !v)}
              >
                <Sparkles size={14} strokeWidth={1.75} aria-hidden />
                {generating ? "Generating…" : "Generate document"}
                <ChevronDown size={13} strokeWidth={2} aria-hidden />
              </Button>
              {genOpen && (
                <div
                  role="menu"
                  className="absolute right-0 z-40 mt-1 w-60 animate-fade-in overflow-hidden rounded-md border border-border bg-surface py-1.5 shadow-pop"
                >
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => void generate("docx")}
                    className="flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-sm text-text hover:bg-surface-2"
                  >
                    <FileText className="h-4 w-4 text-brand-on" strokeWidth={1.75} aria-hidden />
                    Word document (.docx)
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    disabled={!pdfAvailable}
                    onClick={() => void generate("pdf")}
                    className="flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-sm text-text hover:bg-surface-2 disabled:pointer-events-none disabled:opacity-50"
                  >
                    <FileDown className="h-4 w-4 text-danger-on" strokeWidth={1.75} aria-hidden />
                    PDF
                    {!pdfAvailable && <span className="ml-auto text-[11px] text-text-3">unavailable</span>}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={discardOpen}
        title={ready ? "Discard this job description?" : "Delete this job description?"}
        body="The source and every generated document for it are removed. This cannot be undone."
        confirmLabel={ready ? "Discard" : "Delete"}
        danger
        busy={discardBusy}
        onConfirm={() => void deleteJob()}
        onCancel={() => setDiscardOpen(false)}
      />

      {actionError && (
        <p className="rounded-md bg-danger-tint px-4 py-2.5 text-[13px] font-medium text-danger-on">
          {actionError}
        </p>
      )}

      {extracting && (
        <Card>
          <CardBody className="flex flex-col items-center gap-4 py-14 text-center">
            <div className="h-1.5 w-72 overflow-hidden rounded-full bg-surface-3">
              <div className="resume-indeterminate h-full w-1/3 rounded-full bg-brand-500" />
            </div>
            <div className="max-w-xl">
              <h3>Reading the job posting…</h3>
              <p className="mt-2 text-[14px] text-text-2">
                Extracting the position details and sections. This usually takes under 30
                seconds — you can move to other pages and come back any time.
              </p>
            </div>
          </CardBody>
        </Card>
      )}

      {job.status === "failed" && (
        <Card className="border-danger">
          <CardBody>
            <p className="font-semibold text-danger-on">Extraction failed</p>
            <p className="mt-1 text-[14px] text-text-2">{job.error}</p>
            <div className="mt-3 flex gap-2">
              <Button variant="secondary" disabled={retrying} onClick={() => void retryExtraction()}>
                <RotateCcw size={14} strokeWidth={1.75} aria-hidden />
                {retrying ? "Retrying…" : "Try again"}
              </Button>
              <Button variant="secondary" onClick={() => navigate("/jds")}>
                Back to job descriptions
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {ready && (
        <>
          {warnings.length > 0 && !warningsDismissed && (
            <div className="flex items-start gap-3 rounded-lg border border-warn bg-warn-tint px-4 py-3">
              <TriangleAlert
                size={17}
                strokeWidth={1.75}
                aria-hidden
                className="mt-0.5 shrink-0 text-warn-on"
              />
              <div className="min-w-0 flex-1">
                <p className="text-[13.5px] font-bold text-warn-on">Check before generating</p>
                <ul className="mt-1 list-disc pl-5 text-[13px] leading-relaxed text-warn-on">
                  {warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
              <button
                type="button"
                aria-label="Dismiss warnings"
                onClick={() => setWarningsDismissed(true)}
                className="shrink-0 rounded-md p-1 text-warn-on hover:bg-warn-tint"
              >
                <X size={15} strokeWidth={2} aria-hidden />
              </button>
            </div>
          )}

          <div className="grid gap-4 xl:grid-cols-[minmax(0,10fr)_minmax(0,11fr)]">
            <Card className="flex min-w-0 flex-col xl:max-h-[calc(100vh-2rem)]">
              <div className="border-b border-border p-1.5">
                <nav aria-label="JD sections" className="flex gap-1 overflow-x-auto">
                  {TABS.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => setActiveTab(tab.key)}
                      aria-current={activeTab === tab.key ? "true" : undefined}
                      className={cn(
                        "relative flex shrink-0 items-center gap-2.5 rounded-md px-3 py-2.5 text-[13.5px] font-medium transition-colors",
                        activeTab === tab.key
                          ? "bg-brand-tint font-semibold text-brand-on"
                          : "text-text-2 hover:bg-surface-2 hover:text-text",
                      )}
                    >
                      <tab.icon size={16} strokeWidth={1.75} aria-hidden />
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>
              <div className="flex min-h-0 flex-1 flex-col gap-4 p-4 xl:overflow-y-auto">
                {activeTab === "details" && (
                  <SectionCard title="Position details">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="sm:col-span-2">
                        <Label>Position title</Label>
                        <Input
                          value={jd!.title ?? ""}
                          onChange={(e) => updateJd({ title: e.target.value || null })}
                        />
                      </div>
                      <div>
                        <Label>Company</Label>
                        <Input
                          value={jd!.company ?? ""}
                          onChange={(e) => updateJd({ company: e.target.value || null })}
                        />
                      </div>
                      <div>
                        <Label>Location</Label>
                        <Input
                          value={jd!.location ?? ""}
                          onChange={(e) => updateJd({ location: e.target.value || null })}
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Extra details (rendered as label rows under the title)</Label>
                      <DetailsRowsEditor
                        items={jd!.details}
                        onChange={(details) => updateJd({ details })}
                      />
                    </div>
                    {jd!.unmappedContent.length > 0 && (
                      <div>
                        <Label>Unmapped content</Label>
                        <p className="mb-2 text-[13px] text-text-2">
                          The AI couldn&rsquo;t place this source text. Move it into a section,
                          or remove it deliberately.
                        </p>
                        <StringListEditor
                          items={jd!.unmappedContent}
                          onChange={(unmappedContent) => updateJd({ unmappedContent })}
                          multiline
                        />
                      </div>
                    )}
                  </SectionCard>
                )}

                {activeTab === "sections" && (
                  <SectionCard title="Posting sections">
                    <SectionsEditor
                      items={jd!.sections}
                      onChange={(sections) => updateJd({ sections })}
                    />
                  </SectionCard>
                )}

                {activeTab === "contact" && (
                  <SectionCard title="Please Contact">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <Label>Name</Label>
                        <Input
                          value={jd!.contact?.name ?? ""}
                          list="jd-contact-names"
                          onChange={(e) => {
                            const name = e.target.value;
                            const match = jdContacts.find(
                              (c) => c.name?.toLowerCase() === name.trim().toLowerCase(),
                            );
                            updateJd({
                              contact: match
                                ? {
                                    ...match,
                                    telephone: [...match.telephone],
                                    mobile: [...match.mobile],
                                    email: [...match.email],
                                    website: [...match.website],
                                  }
                                : { ...contactBase(), name: name || null },
                            });
                          }}
                        />
                        <datalist id="jd-contact-names">
                          {jdContacts.map((c, i) => (
                            <option key={i} value={c.name ?? ""} />
                          ))}
                        </datalist>
                        {jdContacts.length > 0 && (
                          <p className="mt-1 text-[12px] text-text-3">
                            Picking or typing a name from Settings → JD contacts fills every
                            field below.
                          </p>
                        )}
                      </div>
                      <div>
                        <Label>Title</Label>
                        <Input
                          value={jd!.contact?.title ?? ""}
                          onChange={(e) =>
                            updateJd({ contact: { ...contactBase(), title: e.target.value || null } })
                          }
                        />
                      </div>
                      <div>
                        <Label>Company</Label>
                        <Input
                          value={jd!.contact?.company ?? ""}
                          onChange={(e) =>
                            updateJd({
                              contact: { ...contactBase(), company: e.target.value || null },
                            })
                          }
                        />
                      </div>
                    </div>
                    {(
                      [
                        ["telephone", "Main phone"],
                        ["mobile", "Mobile"],
                        ["email", "Email"],
                        ["website", "Website"],
                      ] as const
                    ).map(([key, label]) => (
                      <div key={key}>
                        <Label>{label}</Label>
                        <StringListEditor
                          items={jd!.contact?.[key] ?? []}
                          onChange={(values) =>
                            updateJd({ contact: { ...contactBase(), [key]: values } })
                          }
                        />
                      </div>
                    ))}
                    <p className="text-[12px] text-text-3">
                      Shown as the &ldquo;Please Contact&rdquo; block at the end of the document
                      — each phone, email and website renders as its own line. Leave everything
                      empty to use the template&rsquo;s fixed contact (set in the template
                      builder); filling any field here overrides it for this job only.
                    </p>
                  </SectionCard>
                )}
              </div>
            </Card>

            <div className="flex min-w-0 flex-col gap-4 self-start xl:sticky xl:top-4">
              <Card>
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-2.5">
                  <div className="min-w-0">
                    <p className="text-[13.5px] font-semibold text-text">Live preview</p>
                    <p className="truncate text-[12px] text-text-2">
                      {job.sourceFilename ?? job.sourceUrl ?? "Pasted text"}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {sourceHref && (
                      <Button asChild variant="secondary" size="sm">
                        <a href={sourceHref} target="_blank" rel="noreferrer">
                          <ExternalLink size={13} strokeWidth={1.75} aria-hidden />
                          Open source
                        </a>
                      </Button>
                    )}
                    <div className="flex items-center rounded-md border border-border">
                      <button
                        type="button"
                        aria-label="Zoom out"
                        disabled={zoom <= 0.5}
                        onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}
                        className="px-1.5 py-1.5 text-text-2 hover:text-text disabled:opacity-40"
                      >
                        <Minus size={14} aria-hidden />
                      </button>
                      <span className="w-11 text-center text-[12px] tabular-nums text-text-2">
                        {Math.round(zoom * 100)}%
                      </span>
                      <button
                        type="button"
                        aria-label="Zoom in"
                        disabled={zoom >= 2}
                        onClick={() => setZoom((z) => Math.min(2, z + 0.25))}
                        className="px-1.5 py-1.5 text-text-2 hover:text-text disabled:opacity-40"
                      >
                        <Plus size={14} aria-hidden />
                      </button>
                    </div>
                  </div>
                </div>
                {templates.length > 0 && (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-border px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <Label className="mb-0 whitespace-nowrap">Template</Label>
                      <Select
                        value={selectedTemplate?.id ?? ""}
                        onChange={(e) => setTemplateId(e.target.value)}
                        className="h-8 w-auto min-w-40"
                        aria-label="Template"
                      >
                        {templates.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name} (v{t.version})
                          </option>
                        ))}
                      </Select>
                    </div>
                  </div>
                )}
                <div className="bg-surface-2 p-3">
                  {selectedTemplate ? (
                    <JdPreview
                      definition={selectedTemplate.definition}
                      logoUrl={selectedTemplate.logoUrl}
                      watermarkUrl={selectedTemplate.watermarkUrl}
                      jd={jd!}
                      zoom={zoom}
                    />
                  ) : (
                    <p className="px-4 py-8 text-center text-[13px] text-text-2">
                      No JD template is published yet — an admin can add one under Templates.
                    </p>
                  )}
                </div>
              </Card>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
